import apiClient from '../utils/request';
import type {
  TraceChainDetail,
  BaseLandPoint,
  FarmingStageItem,
  CertificateItem,
  InspectionReportItem,
} from '../mock/traceData';

export interface AppointmentForm {
  name: string;
  phone: string;
  visitDate: string;
  visitorsCount: number;
  purpose: string;
  notes?: string;
  baseId?: number | string;
  baseName?: string;
}

export interface AppointmentVoucher {
  id: string;
  name: string;
  phone: string;
  visitDate: string;
  visitorsCount: number;
  status: 'confirmed' | 'pending';
  baseName: string;
  qrCodeUrl: string;
  createdAt: string;
}

export const traceService = {
  /**
   * Fetch full traceability chain detail by traceCode from Spring Boot backend
   */
  async getTraceDetail(traceCode?: string): Promise<TraceChainDetail> {
    const code = traceCode || 'MA.156.A1.0058.AG.101/20260826.ORG.0010';
    const data = await apiClient.get<any, TraceChainDetail>('/c/trace/detail', {
      params: { code },
    });
    return data;
  },

  /**
   * Fetch recommended test cases / sample trace codes from backend
   */
  async getSampleCodes(): Promise<Array<{ name: string; code: string }>> {
    try {
      const list = await apiClient.get<any, Array<{ name: string; code: string }>>('/c/trace/samples');
      if (Array.isArray(list) && list.length > 0) return list;
    } catch {}
    return [
      {
        name: '五常龙凤山 · 5kg 有机胚芽米六角礼盒装',
        code: 'MA.156.A1.0058.AG.101/20260826.ORG.0010',
      },
      {
        name: '嫩江平原 · 5kg 富硒生态胚芽米牛皮纸袋装',
        code: 'MA.156.A1.0059.SE.201/20260826.GRN.0025',
      },
      {
        name: '五常金穗 · 2.5kg 家庭双砖真空锁鲜装',
        code: 'MA.156.A1.0058.AG.102/20260822.ORG.0088',
      },
    ];
  },

  /**
   * Report scan log with geo coordinates
   */
  async reportScanLog(logData: {
    code: string;
    productName?: string;
    landName?: string;
    locationCity?: string;
    latitude?: number;
    longitude?: number;
    isAuthentic?: number;
  }) {
    try {
      await apiClient.post('/c/trace/scan-log', logData);
    } catch {}
  },

  /**
   * Get all base lands for map
   * 后端已返回真实边界（boundary.points / center / areaMu），此处原样透传；
   * 旧实现给每个缺失字段都塞了固定值（品种、负责人、经纬度、网图），
   * 导致任何地块看起来都是「五优稻4号 · 李春山 · 五常」，C 端等于展示假数据。
   */
  async getBaseLands(): Promise<BaseLandPoint[]> {
    const list = await apiClient.get<any, any[]>('/c/base/lands');
    if (!Array.isArray(list)) return [];
    return list.map((item: any) => {
      const center = item.center || item.boundary?.center || {};
      const points = Array.isArray(item.boundary?.points) ? item.boundary.points : [];
      const varieties = item.varieties
        ? String(item.varieties).replace(/["\[\]]/g, '').trim()
        : '';
      return {
        id: String(item.id),
        name: item.name || '',
        area: item.area != null ? Number(item.area) : 0,
        variety: varieties,
        soilOrganic: item.soilOrganic != null && item.soilOrganic !== '' ? `${item.soilOrganic} g/kg` : '',
        manager: item.manager || '',
        enterpriseName: item.enterpriseName || '',
        status: item.status || '',
        lat: center.lat != null ? Number(center.lat) : 0,
        lng: center.lng != null ? Number(center.lng) : 0,
        imageUrl: item.imageUrl || '',
        organicYears: item.organicYears != null ? Number(item.organicYears) : 0,
        pesticideFreeDays: item.pesticideFreeDays != null ? Number(item.pesticideFreeDays) : 0,
        code: item.code || '',
        baseName: item.baseName || '',
        soilType: item.soilType || '',
        waterSource: item.waterSource || '',
        address: item.address || '',
        geoAreaMu: item.geoAreaMu != null ? Number(item.geoAreaMu) : null,
        boundaryPoints: points.map((p: any) => ({ lng: Number(p.lng), lat: Number(p.lat) })),
        isCurrent: !!item.isCurrent,
      } as BaseLandPoint;
    });
  },

  /**
   * Get land detail by id
   */
  async getLandDetail(landId: string): Promise<any> {
    return apiClient.get(`/c/base/land/${landId}`);
  },

  /**
   * Submit appointment to backend
   */
  async submitAppointment(form: AppointmentForm): Promise<AppointmentVoucher> {
    const res = await apiClient.post<any, any>('/c/appointment', {
      visitorName: form.name,
      contactPhone: form.phone,
      visitDate: form.visitDate,
      visitorsCount: form.visitorsCount,
      purpose: form.purpose,
      notes: form.notes,
      baseName: form.baseName || '五常市龙凤山水源地国家级有机水稻种植示范基地',
    });

    return {
      id: String(res.id || `APPT-${Date.now().toString().slice(-6)}`),
      name: form.name,
      phone: form.phone,
      visitDate: form.visitDate,
      visitorsCount: form.visitorsCount,
      status: 'confirmed',
      baseName: res.baseName || form.baseName || '五常市龙凤山水源地国家级有机水稻种植示范基地',
      qrCodeUrl: res.qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${res.id}`,
      createdAt: res.createTime || new Date().toLocaleString(),
    };
  },

  /**
   * Get user's appointment records
   */
  async getMyAppointments(consumerId?: number | string): Promise<any[]> {
    return apiClient.get('/c/appointment/my', {
      params: { consumerId },
    });
  },
};
